import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Container } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faExternalLinkAlt, faSpinner } from '@fortawesome/free-solid-svg-icons';
import './ProjectDetail.css';

var API_BASE    = process.env.REACT_APP_API_URL || 'http://localhost:3001/api/v1';
var STATIC_BASE = API_BASE.replace('/api/v1', '');

/* ── Resolve full image URL ───────────────────────────────────── */
function imgSrc(coverImage) {
  if (!coverImage) return null;
  if (coverImage.startsWith('blob:') || coverImage.startsWith('http')) return coverImage;
  // Ensure exactly one slash between base and path
  var path = coverImage.startsWith('/') ? coverImage : '/' + coverImage;
  return STATIC_BASE + path;
}

var fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show:   { opacity: 1, y: 0, transition: { duration: 0.65, ease: [0.16, 1, 0.3, 1] } },
};
var stagger = { hidden: {}, show: { transition: { staggerChildren: 0.1 } } };

var ProjectDetail = function() {
  var params = useParams();
  var id     = parseInt(params.id);

  var projectState = useState(null);  var project = projectState[0]; var setProject = projectState[1];
  var loadingState = useState(true);  var loading = loadingState[0]; var setLoading = loadingState[1];
  var errorState   = useState('');    var error   = errorState[0];   var setError   = errorState[1];

  useEffect(function() {
    if (!id) { setError('Invalid project ID'); setLoading(false); return; }
    fetch(API_BASE + '/projects/' + id)
      .then(function(r) {
        if (!r.ok) throw new Error('Project not found');
        return r.json();
      })
      .then(function(data) { setProject(data); })
      .catch(function(e) { setError(e.message); })
      .finally(function() { setLoading(false); });
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <div className="pdetail-root pdetail-loading">
        <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: '2rem', color: 'var(--clr-gold)' }} />
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="pdetail-root">
        <Container>
          <div className="pdetail-notfound">
            <span className="home-label">404</span>
            <h2>Project Not Found</h2>
            <p>The project you are looking for does not exist or has not been added yet.</p>
            <Link to="/projects" className="home-btn home-btn--outline">
              <FontAwesomeIcon icon={faArrowLeft} /> Back to Projects
            </Link>
          </div>
        </Container>
      </div>
    );
  }

  var resolvedCover = imgSrc(project.coverImage);
  var hasCover      = Boolean(resolvedCover);
  var hasOverview   = Boolean(project.overview);
  var hasFeatures   = project.features  && project.features.length  > 0;
  var hasOutcomes   = project.outcomes  && project.outcomes.length  > 0;
  var hasTechList   = project.technologies && project.technologies.length > 0;
  var hasDetail     = hasOverview || hasFeatures || hasOutcomes;

  return (
    <motion.div className="pdetail-root" initial="hidden" animate="show" variants={stagger}>

      {/* Full-width cover banner */}
      {hasCover && (
        <div className="pdetail-cover" aria-hidden="true">
          <img src={resolvedCover} alt={project.title} />
          <div className="pdetail-cover__overlay" />
        </div>
      )}

      {/* Hero */}
      <section className={'pdetail-hero' + (hasCover ? ' pdetail-hero--has-cover' : '')} aria-label="Project detail header">
        <div className="pdetail-hero__glow" aria-hidden="true" />
        <Container>
          <motion.div variants={fadeUp}>
            <Link to="/projects" className="pdetail-hero__back">
              <FontAwesomeIcon icon={faArrowLeft} /> All Projects
            </Link>
          </motion.div>
          <motion.span className="home-label" variants={fadeUp}>Case Study</motion.span>
          <motion.h1 className="pdetail-hero__title" variants={fadeUp}>
            <em>{project.title}</em>
          </motion.h1>
          <motion.p className="pdetail-hero__desc" variants={fadeUp}>
            {project.description}
          </motion.p>
          {project.viewLink && (
            <motion.div variants={fadeUp}>
              <a href={project.viewLink} className="home-btn home-btn--primary"
                target="_blank" rel="noopener noreferrer">
                View Live Project <FontAwesomeIcon icon={faExternalLinkAlt} />
              </a>
            </motion.div>
          )}
        </Container>
      </section>

      {/* Content */}
      <section className="pdetail-content" aria-label="Project details">
        <Container>
          <div className="pdetail-grid">

            <motion.div variants={stagger}>
              {hasDetail ? (
                <>
                  {hasOverview && (
                    <motion.div className="pdetail-section" variants={fadeUp}>
                      <h2 className="pdetail-section-title">Project Overview</h2>
                      <p className="pdetail-overview">{project.overview}</p>
                    </motion.div>
                  )}
                  {hasFeatures && (
                    <motion.div className="pdetail-section" variants={fadeUp}>
                      <h2 className="pdetail-section-title">Key Features</h2>
                      <ul className="pdetail-list">
                        {project.features.map(function(f, i) { return <li key={i}>{f}</li>; })}
                      </ul>
                    </motion.div>
                  )}
                  {hasOutcomes && (
                    <motion.div className="pdetail-section" variants={fadeUp}>
                      <h2 className="pdetail-section-title">Outcomes &amp; Impact</h2>
                      <ul className="pdetail-list">
                        {project.outcomes.map(function(o, i) { return <li key={i}>{o}</li>; })}
                      </ul>
                    </motion.div>
                  )}
                </>
              ) : (
                <motion.div className="pdetail-section pdetail-section--empty" variants={fadeUp}>
                  <p style={{ color: 'var(--clr-text-subtle)', fontStyle: 'italic' }}>
                    Full case study coming soon.
                  </p>
                </motion.div>
              )}
            </motion.div>

            {/* Sidebar */}
            <motion.aside className="pdetail-sidebar" variants={fadeUp} aria-label="Project metadata">
              <div className="pdetail-sidebar-card">
                {(hasTechList || (project.tags && project.tags.length > 0)) && (
                  <div>
                    <p className="pdetail-sidebar-title">Technologies</p>
                    <div className="pdetail-tech-list">
                      {(hasTechList ? project.technologies : project.tags).map(function(t) {
                        return <span key={t} className="pdetail-tech-pill">{t}</span>;
                      })}
                    </div>
                  </div>
                )}
                {project.viewLink && (
                  <div>
                    <p className="pdetail-sidebar-title">Live Link</p>
                    <a href={project.viewLink} className="pdetail-view-btn"
                      target="_blank" rel="noopener noreferrer">
                      View Project <FontAwesomeIcon icon={faExternalLinkAlt} />
                    </a>
                  </div>
                )}
                <div>
                  <p className="pdetail-sidebar-title">More Projects</p>
                  <Link to="/projects" className="home-btn home-btn--outline"
                    style={{ width: '100%', justifyContent: 'center' }}>
                    <FontAwesomeIcon icon={faArrowLeft} /> Back to All
                  </Link>
                </div>
              </div>
            </motion.aside>

          </div>
        </Container>
      </section>

    </motion.div>
  );
};

export default ProjectDetail;